import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type { Site } from "@/models/site"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import type { UseQueryOptions } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import type { IntervalHourlyUsageProfileResponse } from "./interval-hourly-usage-profile.types"

interface GetIntervalHourlyUsageProfileParams {
  meterIds: string
  organizationId: Organization["id"]
  rangeEnd: string
  rangeStart: string
  siteId: Site["id"]
}

const getIntervalHourlyUsageProfile = async ({
  meterIds,
  organizationId,
  rangeEnd,
  rangeStart,
  siteId,
}: GetIntervalHourlyUsageProfileParams): Promise<IntervalHourlyUsageProfileResponse> => {
  try {
    const response = await httpClient.get<IntervalHourlyUsageProfileResponse>(
      `/api/v1/organizations/${organizationId}/interval_hourly_usage_profile`,
      {
        params: {
          end: rangeEnd,
          meter_ids: meterIds,
          site_id: siteId,
          start: rangeStart,
        },
      }
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseIntervalHourlyUsageProfileOptions {
  meterIds: string
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseQueryOptions<IntervalHourlyUsageProfileResponse, HttpError>,
    "queryKey" | "queryFn"
  >
  rangeEnd: string
  rangeStart: string
  siteId: Site["id"]
}

export const useIntervalHourlyUsageProfile = ({
  meterIds,
  organizationId,
  queryOptions = {},
  rangeEnd,
  rangeStart,
  siteId,
}: UseIntervalHourlyUsageProfileOptions) => {
  const queryKey = useQueryKey(
    ApiQueryName.IntervalHourlyUsageProfile,
    "getOne",
    siteId,
    {
      meterIds,
      organizationId,
      rangeEnd,
      rangeStart,
    }
  )

  const queryResult = useQuery<IntervalHourlyUsageProfileResponse, HttpError>({
    ...queryOptions,
    enabled:
      Boolean(organizationId) &&
      Boolean(siteId) &&
      Boolean(meterIds) &&
      Boolean(rangeStart) &&
      Boolean(rangeEnd) &&
      (queryOptions.enabled ?? true),
    queryFn: () =>
      getIntervalHourlyUsageProfile({
        meterIds,
        organizationId,
        rangeEnd,
        rangeStart,
        siteId,
      }),
    queryKey,
    throwOnError: queryOptions.throwOnError ?? false,
  })

  return useRenameQueryResult(
    queryResult,
    ApiQueryName.IntervalHourlyUsageProfile
  )
}
